export type ClientSupportMessageSender = 'CLIENT' | 'SUPER_ADMIN' | 'SYSTEM' | 'SELLER';

export type ClientSupportSystemEvent =
  | 'ORDER_PROBLEM'
  | 'ORDER_PROBLEM_RESOLVED'
  | 'SELLER_INVITED';

export interface ClientSupportMessage {
  id: number;
  threadId: number;
  senderKind: ClientSupportMessageSender;
  body: string;
  imageUrls: string[];
  senderName: string | null;
  createdAt: string;
  systemEvent?: ClientSupportSystemEvent | null;
  orderId?: number | null;
  orderNumber?: string | null;
  relatedMessageId?: number | null;
  invitedSellerId?: number | null;
  invitedSellerName?: string | null;
}

export interface ClientSupportMessagesResponse {
  items: ClientSupportMessage[];
  total: number;
  threadId: number;
}

export interface ClientSupportThreadSummary {
  threadId: number;
  userId: number;
  userName: string | null;
  userPhone: string | null;
  lastMessage: ClientSupportMessage | null;
  unreadCount: number;
  hasOpenOrderProblem?: boolean;
  updatedAt: string;
}

export interface ClientSupportThreadsResponse {
  items: ClientSupportThreadSummary[];
  total: number;
}

export interface ClientSupportUserSearchItem {
  userId: number;
  name: string | null;
  phone: string | null;
}

export interface ClientSupportChatMessageEvent {
  userId: number;
  userName: string | null;
  userPhone: string | null;
  message: ClientSupportMessage;
  problemMessageId?: number | null;
}

export const CLIENT_SUPPORT_CHAT_WS_NAMESPACE = '/client-support-chat';
export const CLIENT_SUPPORT_CHAT_WS_PATH = '/api/ws';

export const CLIENT_SUPPORT_CHAT_EVENTS = {
  messageNew: 'chat:message',
  unread: 'chat:unread',
} as const;

export function isSystemSupportMessage(message: ClientSupportMessage): boolean {
  return message.senderKind === 'SYSTEM' || Boolean(message.systemEvent);
}

export function isResolvedOrderProblem(
  messages: ClientSupportMessage[],
  problemMessageId: number,
): boolean {
  return messages.some(
    (m) =>
      m.systemEvent === 'ORDER_PROBLEM_RESOLVED' &&
      m.relatedMessageId === problemMessageId,
  );
}

export function isSellerInvitedToOrderProblem(
  messages: ClientSupportMessage[],
  problemMessageId: number,
): boolean {
  const problem = messages.find((m) => m.id === problemMessageId);
  if (problem?.invitedSellerId != null) return true;
  return messages.some(
    (m) =>
      m.systemEvent === 'SELLER_INVITED' &&
      m.relatedMessageId === problemMessageId,
  );
}
